import React, { useEffect } from 'react';

/**
 * Lightweight confirmation modal — replaces window.confirm() across the app.
 * Usage: render when pendingConfirm !== null, clear it on Cancel or after Confirm.
 *
 * Props:
 *   message       — question text
 *   confirmLabel  — button label (default "Confirm")
 *   variant       — "default" (amber) | "danger" (red)
 *   onConfirm     — called when user clicks the confirm button
 *   onCancel      — called on Cancel, Escape, or backdrop click
 */
export default function ConfirmDialog({
  message,
  confirmLabel = 'Confirm',
  variant = 'default',
  onConfirm,
  onCancel,
}) {
  const isDestructive = variant === 'danger';

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="relative w-full max-w-xs rounded-2xl overflow-hidden"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        <div
          className="absolute top-0 left-0 right-0"
          style={{
            height: 2,
            background: isDestructive
              ? 'linear-gradient(90deg, transparent, var(--color-danger), transparent)'
              : 'linear-gradient(90deg, transparent, var(--accent), transparent)',
          }}
        />
        <div className="px-6 pt-7 pb-6">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {message}
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-semibold rounded-xl transition-all"
              style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--text-muted)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-semibold rounded-xl transition-all"
              style={isDestructive
                ? { background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }
                : { background: 'var(--accent-dim)', border: '1px solid var(--border-accent)', color: 'var(--accent)' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
